// src/hooks/useFirebaseMessaging.js
// Custom hook for Firebase Cloud Messaging integration

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  requestNotificationPermission,
  registerTokenWithBackend,
  onForegroundMessage,
  unregisterToken,
} from '../utils/firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FCM_TOKEN_KEY = 'fcm_token';
const FCM_PERMISSION_KEY = 'fcm_permission_status';

export default function useFirebaseMessaging() {
  const [fcmToken, setFcmToken] = useState(() => localStorage.getItem(FCM_TOKEN_KEY));
  const [permissionStatus, setPermissionStatus] = useState(() => {
    return typeof Notification !== 'undefined' ? Notification.permission : 'default';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  // Check if notifications are supported
  const isSupported = typeof window !== 'undefined' && 
    'Notification' in window && 
    'serviceWorker' in navigator;

  /**
   * Sync permission status with browser's native Notification.permission
   */
  const checkAndSyncPermission = useCallback(() => {
    if (typeof Notification === 'undefined') return;
    
    const currentPerm = Notification.permission;
    setPermissionStatus(currentPerm);
    localStorage.setItem(FCM_PERMISSION_KEY, currentPerm);

    // If permission was revoked by user in browser settings, clean up token
    if (currentPerm !== 'granted') {
      const savedToken = localStorage.getItem(FCM_TOKEN_KEY);
      if (savedToken) {
        localStorage.removeItem(FCM_TOKEN_KEY);
        setFcmToken(null);
      }
    }
  }, []);

  // Listen for browser focus and visibility changes to capture setting changes immediately
  useEffect(() => {
    if (!isSupported) return;

    checkAndSyncPermission();

    const handleSync = () => checkAndSyncPermission();

    window.addEventListener('focus', handleSync);
    document.addEventListener('visibilitychange', handleSync);

    return () => {
      window.removeEventListener('focus', handleSync);
      document.removeEventListener('visibilitychange', handleSync);
    };
  }, [isSupported, checkAndSyncPermission]);

  /**
   * Request permission and register token
   */
  const enableNotifications = useCallback(async () => {
    if (!isSupported) {
      setError('المتصفح لا يدعم الإشعارات');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request permission and get token
      const token = await requestNotificationPermission();
      
      if (!token) {
        setPermissionStatus(Notification.permission);
        localStorage.setItem(FCM_PERMISSION_KEY, Notification.permission);
        if (Notification.permission === 'denied') {
          setError('تم رفض إذن الإشعارات. يرجى تفعيلها من إعدادات المتصفح');
        }
        setIsLoading(false);
        return false;
      }

      // Save token locally
      setFcmToken(token);
      localStorage.setItem(FCM_TOKEN_KEY, token);
      setPermissionStatus('granted');
      localStorage.setItem(FCM_PERMISSION_KEY, 'granted');

      // Register with backend
      const authToken = (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '') || (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '');
      if (authToken) {
        await registerTokenWithBackend(token, authToken, BACKEND_URL);
      }

      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError('حدث خطأ أثناء تفعيل الإشعارات');
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  /**
   * Disable notifications
   */
  const disableNotifications = useCallback(async () => {
    const authToken = (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '') || (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '');
    const savedToken = localStorage.getItem(FCM_TOKEN_KEY);

    if (savedToken && authToken) {
      await unregisterToken(savedToken, authToken, BACKEND_URL);
    }

    localStorage.removeItem(FCM_TOKEN_KEY);
    setFcmToken(null);
    setPermissionStatus('default');
    localStorage.setItem(FCM_PERMISSION_KEY, 'default');
  }, []);

  /**
   * Setup foreground message listener
   */
  useEffect(() => {
    if (permissionStatus !== 'granted' || !fcmToken) return;

    let isMounted = true;

    const setupListener = async () => {
      try {
        const unsubscribe = await onForegroundMessage((payload) => {
          if (isMounted) {
            console.log('Foreground notification payload:', payload);
            // Data-only messages: title و body في payload.data
            // Notification messages: في payload.notification (fallback)
            const data = payload.data || {};
            const notification = payload.notification || {};
            
            setLastMessage({
              title: data.title || notification.title || 'إشعار جديد',
              body: data.body || notification.body || '',
              data: data,
              receivedAt: new Date().toISOString(),
            });
          }
        });
        
        if (isMounted) {
          unsubscribeRef.current = unsubscribe;
        }
      } catch (err) {
        console.error('Error setting up message listener:', err);
      }
    };

    setupListener();

    return () => {
      isMounted = false;
      if (typeof unsubscribeRef.current === 'function') {
        unsubscribeRef.current();
      }
    };
  }, [permissionStatus, fcmToken]);

  /**
   * Auto-register token on login
   */
  useEffect(() => {
    const savedToken = localStorage.getItem(FCM_TOKEN_KEY);
    const authToken = (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '') || (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '');
    
    if (savedToken && authToken && permissionStatus === 'granted') {
      registerTokenWithBackend(savedToken, authToken, BACKEND_URL).catch(console.error);
    }
  }, [permissionStatus]);

  /**
   * Clear last message
   */
  const clearLastMessage = useCallback(() => {
    setLastMessage(null);
  }, []);

  return {
    fcmToken,
    permissionStatus,
    isLoading,
    isSupported,
    lastMessage,
    error,
    enableNotifications,
    disableNotifications,
    clearLastMessage,
    isEnabled: permissionStatus === 'granted' && !!fcmToken,
  };
}
